import React from 'react';
import { ManagedUser } from '../../types';
import styles from './UserTable.module.css';

interface UserTableProps {
  users: ManagedUser[];
  onEditUser: (user: ManagedUser) => void;
  onEditRoles: (user: ManagedUser) => void;
  onToggleBlock: (user: ManagedUser) => void;
  onDelete: (user: ManagedUser) => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function statusBadge(user: ManagedUser): React.ReactElement {
  if (user.blocked) {
    return <span className="badge bg-danger">Blocked</span>;
  }
  if (!user.emailVerified) {
    return <span className="badge bg-warning text-dark">Unverified</span>;
  }
  return <span className="badge bg-success">Active</span>;
}

const UserTable: React.FC<UserTableProps> = ({ users, onEditUser, onEditRoles, onToggleBlock, onDelete }) => {
  if (users.length === 0) {
    return <p className="text-muted">No users found.</p>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle">
        <thead className="table-light">
          <tr>
            <th>User</th>
            <th>Status</th>
            <th>Last Login</th>
            <th className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.userId}>
              <td>
                <div className="d-flex align-items-center gap-2">
                  {user.picture ? (
                    <img src={user.picture} alt="" className={styles.avatar} />
                  ) : (
                    <span className={styles.avatarPlaceholder}>
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div>
                    <div className="fw-semibold">{user.name || '—'}</div>
                    <small className="text-muted">{user.email}</small>
                  </div>
                </div>
              </td>
              <td>{statusBadge(user)}</td>
              <td>{formatDate(user.lastLogin)}</td>
              <td className="text-end">
                <button
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={() => onEditUser(user)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={() => onEditRoles(user)}
                >
                  Edit Roles
                </button>
                <button
                  className={`btn btn-sm me-2 ${user.blocked ? 'btn-outline-success' : 'btn-outline-warning'}`}
                  onClick={() => onToggleBlock(user)}
                >
                  {user.blocked ? 'Unblock' : 'Block'}
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => onDelete(user)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;
